// src/tenants/inmocean/config/orderFileSchemas.js

/**
 * Order File Type Constants
 */
export const ORDER_FILE_TYPES = [
    'order_tracking'
    // Ready for additional order file types:
    // 'order_status_updates',
    // 'shipping_confirmations', 
    // 'delivery_tracking'
  ];
  
  /**
   * InMocean Order File Schemas
   * Following the exact same pattern as INMOCEAN_FILE_SCHEMAS for seamless component reuse
   */
  export const INMOCEAN_ORDER_SCHEMAS = {
    order_tracking: {
      name: 'Order Tracking Data',
      description: 'Comprehensive order tracking with customer details, pricing, and status information',
      icon: '📋',
      tableName: 'order_tracking_data',
      maxSizeGB: 2,
      strict: false, // Flexible schema like costing files
      
      // 🎯 Google Sheets Sync Configuration (NEW)
      syncEnabled: true,
      googleSheetUrl: 'https://docs.google.com/spreadsheets/d/1PjCjraQLyCu6AtgOyRW7tu_LgTJ227Bz4zJqiMuHnf8/edit?gid=1854005396#gid=1854005396',
      sheetTabName: 'Order Tracking',
      isOrderFile: true,
      
      // Required columns for basic order processing
      requiredColumns: [
        'order_no_',
        'customer_name', 
        'order_qty',
        'order_date',
        'order_stage'
      ],
      
      // All 57 fields from your order tracking file
      columns: [
        // Order Identification & Management
        'division',
        'order_no_',
        'pick_number', 
        'nsr',
        'nsr_desc',
        'sts',
        
        // Order Details
        'ord_type',
        'ord_src', 
        'order_qty',
        'order_date',
        'backord',
        'comments',
        'hold_rsn',
        
        // Customer Information  
        'customer_name',
        'customer',
        'po_no',
        
        // Dates & Tracking
        'entry_date',
        'estimate_cartons',
        'order_stage',
        'routing_note',
        
        // Organization
        'dept',
        'ending',
        'salesperson_1', 
        'start',
        
        // Product Details
        'style',
        'color',
        'season',
        'season_name',
        'style_name',
        'style_season',
        
        // Packaging & Quantities
        'carton_qty',
        'pack_qty',
        'conf_type',
        'bulk_no',
        'manual_bulk',
        
        // Pricing Information
        'net_price',
        'net_ext_price', 
        'det_ext_ret_price',
        'dtl_ext_royalty',
        'detail_cost',
        'detail_ext_cost',
        
        // Discounts & Allowances
        'dtl_line_disc',
        'dtl_ext_line_disc',
        'dtl_net_ext_alw',
        'dtl_net_alw',
        
        // Profitability Metrics
        'ext_gp_sss__std_cost_',
        'gp__',
        'gp_sss____std_cost_',
        'gross_profit',
        
        // Location & Logistics
        'loc',
        'program',
        'category',
        
        // Labeling & Documentation
        'label_code',
        'label',
        'label_name',
        
        // Reporting & Invoice
        'mabd',
        'rpt_date', 
        'invoice_date',
        'total_amt_inv',
        
        // Additional Classifications
        'use_fct',
        'type',
        'type_name'
      ],
      
      // Validation rules specific to order tracking
      validationRules: {
        dateFields: [
          'order_date', 
          'entry_date', 
          'invoice_date', 
          'rpt_date'
        ],
        numericFields: [
          'order_qty', 
          'estimate_cartons', 
          'carton_qty', 
          'pack_qty',
          'net_price', 
          'net_ext_price', 
          'det_ext_ret_price', 
          'dtl_ext_royalty',
          'detail_cost', 
          'detail_ext_cost', 
          'dtl_line_disc', 
          'dtl_ext_line_disc',
          'dtl_net_ext_alw', 
          'dtl_net_alw', 
          'gross_profit', 
          'total_amt_inv'
        ],
        statusFields: [
          'order_stage', 
          'sts', 
          'ord_type', 
          'backord'
        ],
        requiredForProcessing: [
          'order_no_', 
          'customer_name', 
          'order_qty'
        ],
        uniqueIdentifiers: [
          'order_no_', 
          'pick_number'
        ],
        maxLength: {
          'comments': 500,
          'routing_note': 500,
          'hold_rsn': 200,
          'nsr_desc': 300
        }
      },
      
      // BigQuery configuration - separate dataset for orders
      bigQueryConfig: {
        dataset: 'inmocean_orders',           // Separate from 'inmocean_raw'
        table: 'order_tracking_data',
        partitionField: 'order_date',         // Partition by order date for performance
        clusterFields: ['customer', 'order_stage', 'style', 'division']
      }
    }
  
    // Ready for additional order file types:
    /*
    order_status_updates: {
      name: 'Order Status Updates',
      description: 'Real-time order status changes and milestone tracking',
      icon: '🔄',
      tableName: 'order_status_updates_data',
      maxSizeGB: 1,
      strict: false,
      syncEnabled: true, // Enable sync for status updates
      googleSheetUrl: 'https://docs.google.com/spreadsheets/d/1PjCjraQLyCu6AtgOyRW7tu_LgTJ227Bz4zJqiMuHnf8/edit?gid=0#gid=0',
      sheetTabName: 'Status Updates',
      isOrderFile: true,
      requiredColumns: ['order_no_', 'status', 'timestamp'],
      columns: [
        'order_no_', 'status', 'timestamp', 'updated_by', 
        'notes', 'milestone', 'location'
      ],
      bigQueryConfig: {
        dataset: 'inmocean_orders',
        table: 'order_status_updates_data',
        partitionField: 'timestamp',
        clusterFields: ['order_no_', 'status']
      }
    },
  
    shipping_confirmations: {
      name: 'Shipping Confirmations', 
      description: 'Shipping confirmations and tracking numbers',
      icon: '📦',
      tableName: 'shipping_confirmations_data',
      maxSizeGB: 1,
      strict: false,
      syncEnabled: true, // Enable sync for shipping
      googleSheetUrl: 'https://docs.google.com/spreadsheets/d/1PjCjraQLyCu6AtgOyRW7tu_LgTJ227Bz4zJqiMuHnf8/edit?gid=2#gid=2',
      sheetTabName: 'Shipping',
      isOrderFile: true,
      requiredColumns: ['order_no_', 'tracking_number', 'ship_date'],
      columns: [
        'order_no_', 'tracking_number', 'ship_date', 'carrier',
        'service_level', 'weight', 'dimensions', 'destination'
      ],
      bigQueryConfig: {
        dataset: 'inmocean_orders',
        table: 'shipping_confirmations_data', 
        partitionField: 'ship_date',
        clusterFields: ['order_no_', 'carrier']
      }
    },
  
    delivery_tracking: {
      name: 'Delivery Tracking',
      description: 'Delivery confirmations and customer receipt verification', 
      icon: '✅',
      tableName: 'delivery_tracking_data',
      maxSizeGB: 1,
      strict: false,
      syncEnabled: false, // Upload only for delivery tracking
      isOrderFile: true,
      requiredColumns: ['order_no_', 'delivery_date', 'status'],
      columns: [
        'order_no_', 'delivery_date', 'status', 'recipient',
        'signature', 'delivery_notes', 'photo_proof'
      ],
      bigQueryConfig: {
        dataset: 'inmocean_orders',
        table: 'delivery_tracking_data',
        partitionField: 'delivery_date', 
        clusterFields: ['order_no_', 'status']
      }
    }
    */
  };
  
  /**
   * Order Processing Status Definitions
   * Maps order_stage values to UI display properties
   */
  export const ORDER_STAGES = {
    'NEW': { 
      label: 'New Order', 
      color: 'blue', 
      bgColor: 'bg-blue-50', 
      textColor: 'text-blue-700',
      priority: 1 
    },
    'CONFIRMED': { 
      label: 'Confirmed', 
      color: 'green', 
      bgColor: 'bg-green-50', 
      textColor: 'text-green-700',
      priority: 2 
    },
    'PROCESSING': { 
      label: 'Processing', 
      color: 'yellow', 
      bgColor: 'bg-yellow-50', 
      textColor: 'text-yellow-700',
      priority: 3 
    },
    'PRODUCTION': { 
      label: 'In Production', 
      color: 'orange', 
      bgColor: 'bg-orange-50', 
      textColor: 'text-orange-700',
      priority: 4 
    },
    'QUALITY_CHECK': { 
      label: 'Quality Check', 
      color: 'purple', 
      bgColor: 'bg-purple-50', 
      textColor: 'text-purple-700',
      priority: 5 
    },
    'PACKAGING': { 
      label: 'Packaging', 
      color: 'indigo', 
      bgColor: 'bg-indigo-50', 
      textColor: 'text-indigo-700',
      priority: 6 
    },
    'SHIPPING': { 
      label: 'Shipping', 
      color: 'violet', 
      bgColor: 'bg-violet-50', 
      textColor: 'text-violet-700',
      priority: 7 
    },
    'DELIVERED': { 
      label: 'Delivered', 
      color: 'emerald', 
      bgColor: 'bg-emerald-50', 
      textColor: 'text-emerald-700',
      priority: 8 
    },
    'COMPLETED': { 
      label: 'Completed', 
      color: 'emerald', 
      bgColor: 'bg-emerald-50', 
      textColor: 'text-emerald-700',
      priority: 9 
    },
    'CANCELLED': { 
      label: 'Cancelled', 
      color: 'red', 
      bgColor: 'bg-red-50', 
      textColor: 'text-red-700',
      priority: 0 
    },
    'HOLD': { 
      label: 'On Hold', 
      color: 'gray', 
      bgColor: 'bg-gray-50', 
      textColor: 'text-gray-700',
      priority: 0 
    },
    'BACKORDER': { 
      label: 'Backorder', 
      color: 'amber', 
      bgColor: 'bg-amber-50', 
      textColor: 'text-amber-700',
      priority: 0 
    }
  };
  
  /**
   * Order File Upload Configuration
   * Matches the pattern used in costing files for consistency
   */
  export const ORDER_UPLOAD_CONFIG = {
    maxFileSize: 2 * 1024 * 1024 * 1024, // 2GB to match order_tracking maxSizeGB
    allowedFormats: ['.csv', '.xlsx', '.xls'],
    validationLevel: 'enhanced', // Use enhanced validation like costing files
    
    // Cloud Storage configuration
    storage: {
      bucketPrefix: 'inmocean-orders-raw', // Separate bucket from costing data
      path: 'order_tracking' // Will create: inmocean-orders-raw/order_tracking/
    },
    
    // BigQuery settings
    bigQuery: {
      dataset: 'inmocean_orders', // Separate dataset from 'inmocean_raw'
      location: 'US',
      writeMode: 'WRITE_APPEND' // Append new data, don't replace
    },
    
    // Processing configuration
    processing: {
      batchSize: 500, // Same as costing files
      timeout: 540, // 9 minutes like costing files
      memory: '8GiB', // Same resource allocation
      cpu: 2
    }
  };

  // 🎯 Google Sheets Sync Functions (NEW)
  
  /**
   * Check if an order file type should use Google Sheets sync
   */
  export const isOrderSyncFileType = (fileType) => {
    const schema = INMOCEAN_ORDER_SCHEMAS[fileType];
    return schema && schema.syncEnabled === true;
  };

  /**
   * Get the Google Sheet configuration for a specific order file type
   */
  export const getOrderSyncConfig = (fileType) => {
    const schema = INMOCEAN_ORDER_SCHEMAS[fileType];
    if (schema && schema.syncEnabled) {
      return {
        url: schema.googleSheetUrl,
        tabName: schema.sheetTabName,
        sheetId: extractSheetId(schema.googleSheetUrl),
        gid: extractGid(schema.googleSheetUrl)
      };
    }
    return null;
  };

  /**
   * Extract sheet ID from Google Sheets URL
   */
  const extractSheetId = (url) => {
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null;
  };

  /**
   * Extract GID (tab ID) from Google Sheets URL
   */
  const extractGid = (url) => {
    const match = url.match(/[#&]gid=([0-9]+)/);
    return match ? match[1] : '0';
  };
  
  /**
   * Integration Utilities
   * Helper functions for seamless integration with existing components
   */
  export const OrderSchemaUtils = {
    /**
     * Convert order schema to match the format expected by existing FileUploadCard
     */
    getSchemaForFileUpload: (orderFileType) => {
      const orderSchema = INMOCEAN_ORDER_SCHEMAS[orderFileType];
      if (!orderSchema) return null;
      
      return {
        ...orderSchema,
        // Ensure compatibility with existing validation service
        maxSizeGB: orderSchema.maxSizeGB,
        requiredColumns: orderSchema.requiredColumns,
        columns: orderSchema.columns
      };
    },
  
    /**
     * Get all order file types in the same format as FILE_TYPES 
     */
    getOrderFileTypes: () => ORDER_FILE_TYPES,
  
    /**
     * Check if a file type is an order file
     */
    isOrderFileType: (fileType) => ORDER_FILE_TYPES.includes(fileType),
  
    /**
     * Get BigQuery configuration for order files
     */
    getBigQueryConfig: (orderFileType) => {
      const schema = INMOCEAN_ORDER_SCHEMAS[orderFileType];
      return schema ? schema.bigQueryConfig : null;
    },

    // 🎯 NEW: Google Sheets sync utilities
    /**
     * Get Google Sheets sync configuration for order files
     */
    getOrderSyncConfig: getOrderSyncConfig,

    /**
     * Get files that support sync vs upload
     */
    getSyncEnabledOrderFiles: () => {
      return ORDER_FILE_TYPES.filter(fileType => isOrderSyncFileType(fileType));
    },

    getUploadOnlyOrderFiles: () => {
      return ORDER_FILE_TYPES.filter(fileType => !isOrderSyncFileType(fileType));
    }
  };

  // Debug logging
  console.log('📦 Order files with sync enabled:', OrderSchemaUtils.getSyncEnabledOrderFiles());
  console.log('📁 Order files with upload only:', OrderSchemaUtils.getUploadOnlyOrderFiles());